/**
 * Marketing site -> app origin handoff.
 *
 * See go/index.html for why this is a page and not a link built in place.
 *
 * localStorage is per-origin, and the site and the app are now different origins.
 * Everything the site writes for the app to read back has to cross here or it is
 * simply lost — which is not a visible failure, it is a silently worse product:
 * a signup that re-asks for birth data already entered, ad conversions that stop
 * attributing, a Dutch visitor landing in an English app.
 *
 * Query: ?to=<app route>, e.g. ?to=%2Fchat — a rooted path on the app origin,
 * never a URL. "//evil.com" and "https://evil.com" both fail the pattern, so a
 * crafted ?to cannot point this at another site; anything unrecognised falls
 * back to the app's own default route.
 */
(function () {
  "use strict";

  var APP = (window.GHD_APP && window.GHD_APP.APP_ORIGIN) || "https://app.gethumandesign.com";

  // Null when app-config.js did not load: this page then carries the keys below
  // across but claims no session, which is the signed-out path — never a wrong
  // key, and never a wrong claim.
  var SESSION_KEY = window.GHD_APP ? window.GHD_APP.sessionKey() : null;

  /**
   * Site-origin keys the app reads back, and what is lost without each.
   *
   * Spelled out rather than read from GHD.KEYS (dashboard/js/storage-keys.js):
   * this page does not load the dashboard bundle, and after /dashboard/ retires
   * that file will not be on this origin at all. GHD.KEYS stays the registry of
   * record — js/head-init.js and js/landing.js spell the same keys out for the
   * same reason, so a rename has to happen in all of them at once.
   */
  var CARRIED_KEYS = [
    // The homepage signup funnel: birth details and email captured before the
    // account existed. Without these the app cannot auto-complete onboarding and
    // asks the user for everything they just typed in.
    "ghd_signup_birth_data",
    "ghd_signup_email",
    // Google Ads click id. Read by dashboard/js/api.js at signup and written to
    // profiles.gclid; it is what makes the signup and pro-upgrade conversions
    // attributable. Losing it degrades smart bidding with no visible error.
    "ghd_gclid",
    // Share-loop attribution: which shared chart or conversation brought this
    // person in. Both share pages route their signed-out call to action through
    // here for this key alone — a link straight to the app origin leaves it
    // behind, and then no signup is ever credited to the person who shared.
    "ghd_share",
    // Signup attribution: the first page opened on this origin, the latest arrival
    // from another site, and the page whose signup form was submitted
    // (js/arrival-touch.js, js/landing.js).
    "ghd_entry",
    "ghd_last_touch",
    "ghd_signup_page",
    // The landing page's locale, so a visitor from /nl/ gets a Dutch app.
    "ghd_ui_lang",
    "ghd_ui_lang_seed",
    // Cosmetic, but jarring to lose mid-signup.
    "hd-theme",
    "fx_selected_currency",
    // One device id per device, not one per origin.
    "ghd_device_id",
  ];

  /**
   * The requested app route, accepted only as a single-slash rooted path.
   *
   * The leading "/" must not be followed by another: "//evil.com" is a
   * protocol-relative URL, and concatenated onto the origin it would send the
   * session payload below to whatever host the attacker named.
   */
  function isRootedRoute(to) {
    return /^\/(?!\/)[A-Za-z0-9\-_/?=&.%]*$/.test(to);
  }

  function requestedRoute() {
    var params = new URLSearchParams(location.search);
    var to = params.get("to") || "";
    if (!isRootedRoute(to)) return "/";
    // Anything else in the query belongs to the route: the local nginx mirror
    // of the /dashboard/ redirect appends the legacy URL's query raw, so
    // `?checkout=success&session_id=…` arrives as siblings of `to` rather than
    // inside it. Re-serialised, so nothing can smuggle a second origin in.
    params.delete("to");
    var extra = params.toString();
    if (extra) to += (to.indexOf("?") === -1 ? "?" : "&") + extra;
    return to;
  }

  /**
   * Whether this origin holds a usable Supabase session.
   *
   * A boolean, never the session itself. The session used to travel in the URL
   * fragment below, on the theory that a fragment is never sent to a server —
   * but a URL leaks in more places than request logs: OxygenOS was observed
   * (2026-08-23) writing the foreground Custom Tab's URL into the Android
   * system log, tokens and all, where any log reader could lift a week-long
   * access_token and its refresh_token; the same URL enters browser history,
   * session restore, and whatever syncs either. So the fragment now carries
   * only this flag, and the app fetches the session over a channel that never
   * touches a URL: it embeds /go/bridge/ (this origin) in a hidden iframe,
   * which posts the session to the app origin via postMessage — see
   * go/bridge.js and dashboard/js/app.js _fetchSessionFromSite.
   *
   * An expired access_token still counts — the refresh token is the part that
   * matters, and api.js already refreshes and retries on a 401.
   */
  function hasStoredSession() {
    try {
      var raw = SESSION_KEY && localStorage.getItem(SESSION_KEY);
      if (!raw) return false;
      var parsed = JSON.parse(raw);
      return !!(parsed && parsed.access_token && parsed.refresh_token);
    } catch (e) {
      return false;
    }
  }

  /** Whichever carried keys this origin actually holds. */
  function carriedValues() {
    var out = {};
    for (var i = 0; i < CARRIED_KEYS.length; i++) {
      try {
        var v = localStorage.getItem(CARRIED_KEYS[i]);
        if (v !== null) out[CARRIED_KEYS[i]] = v;
      } catch (e) {
        /* storage disabled — carry what we can */
      }
    }
    return out;
  }

  var route = requestedRoute();
  var payload = { k: carriedValues() };

  // Moved, not copied: the app spends these at its next onboarding, and a copy
  // left here would ride along again and credit a later account on this device
  // with the first one's arrival page.
  var MOVED_KEYS = ["ghd_entry", "ghd_last_touch", "ghd_signup_page"];
  for (var m = 0; m < MOVED_KEYS.length; m++) {
    try {
      localStorage.removeItem(MOVED_KEYS[m]);
    } catch (e) {
      /* storage disabled — nothing was kept to leave behind */
    }
  }
  // "session crosses": a flag, not the session — see hasStoredSession above.
  // No token, no credential, nothing in this payload may authenticate anything:
  // the URL it rides in ends up in Android's system log and in browser history.
  var sessionFlag = hasStoredSession();
  if (sessionFlag) payload.sc = 1;

  // Nothing to carry: a plain link is honest, and keeps a pointless fragment out
  // of the address bar of every signed-out visitor.
  var hasPayload = sessionFlag || Object.keys(payload.k).length > 0;

  var target = APP + route;
  if (hasPayload) {
    // Payload in the fragment — never sent to a server, never in a request log,
    // which matters because it carries birth details. The route is simply the
    // path now that the app routes on paths, so there is nothing left competing
    // for the fragment; dashboard/js/app.js reads the payload and strips it
    // before the first render.
    target = APP + route + "#ghd_handoff=" + encodeURIComponent(JSON.stringify(payload));
  }

  // A fallback link, in case a redirect is blocked or the app origin is slow to
  // answer — a dead-end "Opening the app…" would be worse than an extra click.
  var fallback = document.getElementById("go-fallback");
  var fallbackLink = document.getElementById("go-fallback-link");
  var noscriptLink = document.getElementById("go-fallback-noscript");
  // Deliberately the payload-free URL: this one sits in the DOM to be copied.
  if (fallbackLink) fallbackLink.href = APP + route;
  if (noscriptLink) noscriptLink.href = APP + route;
  setTimeout(function () {
    if (fallback) fallback.hidden = false;
  }, 2500);

  // replace(), not assign(): the back button should return to the page the user
  // came from, not bounce them through this redirect again.
  location.replace(target);
})();
